// In-app feedback / brightmark API client (#1998).
//
// Wraps `POST /api/v1/feedback`. The browser sends only NON-AUTHORITATIVE client hints (route, app
// version, viewport, locale…). The server derives identity/business, redacts PII, uploads the
// screenshot and raises the GitHub issue — the GitHub token never reaches the browser.
//
// Multipart is mandatory (the optional screenshot rides the `media` field), so this goes through
// `fetchWithAuthRetry` rather than the JSON `api` helper: the browser must set the multipart
// boundary itself, which it only does when no Content-Type header is supplied.

import { api, fetchWithAuthRetry, getBaseUrl } from './client'
import { useAuthStore } from '@/stores/useAuthStore'
import type { BrightmarkAnnotation } from '@/lib/brightmark/types'

/** The four friendly feedback tiles → backend `type` values. */
export type FeedbackType = 'bug' | 'data_integrity' | 'suggestion' | 'feature'

/** Backend status values for a feedback row (mirrors `FeedbackStatus` in app/models/feedback.py). */
export type FeedbackStatus = 'queued' | 'submitted' | 'mock' | 'failed'

/**
 * Advisory environment hints. These six keys ARE the contract — the backend whitelists to exactly
 * this set (app/services/feedback/client_hints.py) and drops anything else, so adding a field here
 * without adding it there means it silently never arrives.
 */
export interface FeedbackClientHints {
  viewport?: string
  screen?: string
  locale?: string
  online?: boolean
  platform?: string
  timezone?: string
}

export interface SubmitFeedbackInput {
  type: FeedbackType
  title?: string
  description: string
  route?: string
  app_version?: string
  local_time?: string
  client_hints?: FeedbackClientHints
  /** Optional screenshot (the snapdom element raster, or a user-picked file). */
  media?: File | Blob | null
  /**
   * Optional brightmark visual annotation (point-and-click / text-select). Serialised to the
   * multipart `annotation` field as a JSON string, matching the backend contract
   * (app/services/feedback/annotation.py). Unknown keys are dropped server-side; submission
   * still 201s.
   */
  annotation?: BrightmarkAnnotation | null
}

export interface FeedbackResult {
  id: string
  status: FeedbackStatus
  issue_number: number | null
  issue_url: string | null
  reference: string
  message: string
}

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) || 'unknown'

export class FeedbackApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'FeedbackApiError'
    this.status = status
  }
}

/** Auth + business-context headers. No Content-Type — the browser sets the multipart boundary. */
function multipartHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const user = useAuthStore.getState().user
  const businessId = (user as Record<string, unknown> | null)?.default_business_id as
    | string
    | undefined
  if (businessId) headers['X-Business-ID'] = businessId
  return headers
}

/** Best-effort, dependency-free capture of non-authoritative client hints. */
export function captureClientHints(): FeedbackClientHints {
  const hints: FeedbackClientHints = {}
  try {
    hints.viewport = `${window.innerWidth}x${window.innerHeight}`
    hints.screen = `${window.screen?.width}x${window.screen?.height}`
    hints.locale = navigator.language
    hints.online = navigator.onLine
    hints.platform = navigator.userAgent
    hints.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    /* non-fatal — hints are advisory */
  }
  return hints
}

/** Submit feedback. Always multipart so an optional screenshot can ride along. */
export async function submitFeedback(input: SubmitFeedbackInput): Promise<FeedbackResult> {
  const form = new FormData()
  form.append('type', input.type)
  form.append('description', input.description)
  if (input.title) form.append('title', input.title)
  form.append('route', input.route ?? window.location.pathname)
  form.append('app_version', input.app_version ?? APP_VERSION)
  form.append('local_time', input.local_time ?? new Date().toString())
  form.append('client_hints', JSON.stringify(input.client_hints ?? captureClientHints()))
  if (input.annotation) form.append('annotation', JSON.stringify(input.annotation))
  if (input.media) form.append('media', input.media, 'element-screenshot.png')

  const response = await fetchWithAuthRetry((token) =>
    fetch(`${getBaseUrl()}/feedback`, {
      method: 'POST',
      headers: multipartHeaders(token),
      body: form,
    })
  )

  if (!response.ok) {
    let detail = `Could not submit feedback (${response.status}).`
    if (response.status === 429) {
      detail = 'Too many reports just now — please wait a minute and try again.'
    } else {
      const data = await response.json().catch(() => null)
      const raw = (data as { detail?: unknown } | null)?.detail
      if (typeof raw === 'string') detail = raw
    }
    throw new FeedbackApiError(detail, response.status)
  }

  return (await response.json()) as FeedbackResult
}

/** A single row of the caller's own past feedback submissions (#2548). */
export interface MyFeedbackRow {
  id: string
  feedback_type: FeedbackType
  title: string
  description: string
  status: FeedbackStatus
  route: string | null
  has_annotation: boolean
  github_issue_number: number | null
  github_issue_url: string | null
  github_state: 'open' | 'closed' | null
  created_at: string
}

export interface MyFeedbackListResponse {
  data: MyFeedbackRow[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

/** Self-service, paginated read of the current user's own feedback submissions. */
export async function listMyFeedback(
  limit: number,
  offset: number
): Promise<MyFeedbackListResponse> {
  const res = await api.get<MyFeedbackListResponse>('/feedback', {
    params: { limit, offset },
  })
  return res as unknown as MyFeedbackListResponse
}
